/**
 * AuthManager
 * Core authentication manager with automatic token refresh
 */
import { AuthService } from '../services/users/AuthService';
import { initialAuthState, authReducer, } from './types';
/**
 * AuthManager
 * Manages authentication state with automatic token refresh
 */
export class AuthManager {
    constructor(http, config = {}) {
        this.stateAdapter = null;
        this.refreshTimer = null;
        this.state = { ...initialAuthState };
        this.listeners = new Set();
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.consecutiveRefreshFailures = 0;
        this.http = http;
        this.authService = new AuthService(http);
        this.config = {
            refreshThresholdSeconds: 300,
            autoRefresh: true,
            refreshCheckIntervalMs: 30000,
            maxRefreshRetries: 2,
            refreshRetryDelayMs: 2000,
            ...config,
        };
        this.storage = config.storage || null;
        // Register 401 response interceptor as safety net
        this.setup401Interceptor();
    }
    /**
     * Connect to a state management adapter
     */
    connectStateAdapter(adapter) {
        this.stateAdapter = adapter;
        // Sync initial state
        const externalState = adapter.getState();
        if (externalState.user) {
            this.state = externalState;
        }
    }
    /**
     * Initialize auth from storage
     */
    async initialize() {
        this.dispatch({ type: 'AUTH_INIT_START' });
        try {
            let user = null;
            if (this.storage) {
                user = await this.storage.getUser();
            }
            if (user && this.isTokenExpired(user)) {
                // Token expired, try to refresh
                if (user.refreshToken) {
                    try {
                        user = await this.refreshToken({ refreshToken: user.refreshToken });
                    }
                    catch {
                        // Refresh failed, clear user
                        user = null;
                        if (this.storage) {
                            await this.storage.removeUser();
                        }
                    }
                }
                else {
                    // No refresh token, clear user
                    user = null;
                    if (this.storage) {
                        await this.storage.removeUser();
                    }
                }
            }
            this.dispatch({ type: 'AUTH_INIT_COMPLETE', payload: user });
            if (user) {
                this.http.setAuthToken(user.accessToken || '');
                this.startAutoRefresh();
            }
            return user;
        }
        catch (error) {
            this.dispatch({ type: 'AUTH_INIT_COMPLETE', payload: null });
            return null;
        }
    }
    /**
     * Login with credentials
     */
    async login(request) {
        this.dispatch({ type: 'LOGIN_START' });
        try {
            const user = await this.authService.login({
                email: request.email,
                password: request.password,
                rememberMe: request.rememberMe ?? false,
            });
            await this.handleAuthSuccess(user);
            this.dispatch({ type: 'LOGIN_SUCCESS', payload: user });
            this.consecutiveRefreshFailures = 0;
            this.config.onLogin?.(user);
            return user;
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Login failed';
            this.dispatch({ type: 'LOGIN_FAILURE', payload: message });
            this.config.onError?.(error instanceof Error ? error : new Error(message));
            throw error;
        }
    }
    /**
     * Refresh access token
     */
    async refreshToken(request) {
        // Prevent multiple simultaneous refresh requests
        if (this.isRefreshing && this.refreshPromise) {
            const result = await this.refreshPromise;
            if (!result) {
                throw new Error('Token refresh failed');
            }
            return result;
        }
        this.isRefreshing = true;
        this.refreshPromise = this.doRefreshToken(request);
        try {
            const result = await this.refreshPromise;
            if (!result) {
                throw new Error('Token refresh failed');
            }
            return result;
        }
        finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }
    async doRefreshToken(request) {
        this.dispatch({ type: 'REFRESH_START' });
        try {
            const user = await this.authService.refreshToken({
                refreshToken: request.refreshToken,
            });
            await this.handleAuthSuccess(user);
            this.dispatch({ type: 'REFRESH_SUCCESS', payload: user });
            this.consecutiveRefreshFailures = 0;
            this.config.onRefresh?.(user);
            return user;
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Token refresh failed';
            this.dispatch({ type: 'REFRESH_FAILURE', payload: message });
            this.config.onError?.(error instanceof Error ? error : new Error(message));
            return null;
        }
    }
    /**
     * Logout current user
     */
    async logout() {
        this.stopAutoRefresh();
        try {
            // Call logout endpoint
            await this.authService.logout();
        }
        catch {
            // Ignore logout API errors
        }
        // Clear local state
        this.http.setAuthToken('');
        if (this.storage) {
            await this.storage.removeUser();
        }
        this.dispatch({ type: 'LOGOUT' });
        this.config.onLogout?.();
    }
    /**
     * Get current auth state
     */
    getState() {
        return this.stateAdapter?.getState() || this.state;
    }
    /**
     * Get current user
     */
    getUser() {
        return this.getState().user;
    }
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        return this.getState().isAuthenticated;
    }
    /**
     * Check if user has specific role
     */
    hasRole(role) {
        const user = this.getUser();
        return user?.roles?.includes(role) ?? false;
    }
    /**
     * Check if user has specific right/permission
     */
    hasRight(right) {
        const user = this.getUser();
        return user?.rights?.includes(right) ?? false;
    }
    /**
     * Check if user has any of the specified roles
     */
    hasAnyRole(roles) {
        return roles.some((role) => this.hasRole(role));
    }
    /**
     * Check if user has all specified roles
     */
    hasAllRoles(roles) {
        return roles.every((role) => this.hasRole(role));
    }
    /**
     * Subscribe to state changes
     */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    /**
     * Force token refresh
     */
    async forceRefresh() {
        const user = this.getUser();
        if (!user?.refreshToken) {
            return null;
        }
        return this.refreshToken({ refreshToken: user.refreshToken });
    }
    /**
     * Apply a LoggedOnUser obtained from an external authentication flow
     * (e.g. Planning Centre OAuth) to the AuthManager. Persists the user
     * to storage, sets the HTTP bearer token, starts auto-refresh, and
     * dispatches LOGIN_SUCCESS — same end state as a password login.
     */
    async applyExternalLogin(user) {
        await this.handleAuthSuccess(user);
        this.dispatch({ type: 'LOGIN_SUCCESS', payload: user });
        this.consecutiveRefreshFailures = 0;
        this.config.onLogin?.(user);
        return user;
    }
    /**
     * Update user data locally (without API call)
     */
    updateUser(updates) {
        this.dispatch({ type: 'UPDATE_USER', payload: updates });
        const user = this.getUser();
        if (user && this.storage) {
            this.storage.setUser(user);
        }
    }
    /**
     * Clear current error
     */
    clearError() {
        this.dispatch({ type: 'CLEAR_ERROR' });
    }
    /**
     * Destroy the auth manager
     */
    destroy() {
        this.stopAutoRefresh();
        this.listeners.clear();
        this.stateAdapter = null;
    }
    // Private methods
    async handleAuthSuccess(user) {
        // Update HTTP client token
        this.http.setAuthToken(user.accessToken || '');
        // Persist to storage
        if (this.storage) {
            await this.storage.setUser(user);
        }
        // Start auto refresh
        this.startAutoRefresh();
    }
    isTokenExpired(user) {
        if (!user.expiresAt) {
            return true;
        }
        const expiresAt = new Date(user.expiresAt).getTime();
        const now = Date.now();
        return now >= expiresAt;
    }
    shouldRefreshToken(user) {
        if (!user.expiresAt || !user.refreshToken) {
            return false;
        }
        const expiresAt = new Date(user.expiresAt).getTime();
        const now = Date.now();
        const thresholdMs = this.config.refreshThresholdSeconds * 1000;
        // Refresh if within threshold of expiry
        return now >= expiresAt - thresholdMs;
    }
    startAutoRefresh() {
        if (!this.config.autoRefresh) {
            return;
        }
        this.stopAutoRefresh();
        this.refreshTimer = setInterval(async () => {
            const user = this.getUser();
            if (user && this.shouldRefreshToken(user)) {
                const maxRetries = this.config.maxRefreshRetries ?? 2;
                const retryDelay = this.config.refreshRetryDelayMs ?? 2000;
                let lastError = null;
                for (let attempt = 0; attempt <= maxRetries; attempt++) {
                    try {
                        await this.refreshToken({ refreshToken: user.refreshToken });
                        // Success - reset failure counter and exit retry loop
                        this.consecutiveRefreshFailures = 0;
                        return;
                    }
                    catch (error) {
                        lastError = error instanceof Error ? error : new Error(String(error));
                        console.warn(`[AuthManager] Auto refresh attempt ${attempt + 1}/${maxRetries + 1} failed:`, lastError.message);
                        // Wait before retrying (skip delay on last attempt)
                        if (attempt < maxRetries) {
                            await new Promise(resolve => setTimeout(resolve, retryDelay));
                            // Re-check user in case state changed during delay
                            const freshUser = this.getUser();
                            if (!freshUser?.refreshToken) {
                                console.warn('[AuthManager] User or refresh token no longer available, aborting retries');
                                return;
                            }
                        }
                    }
                }
                // All retries exhausted - force logout
                console.error(`[AuthManager] All ${maxRetries + 1} refresh attempts failed. Forcing logout.`);
                this.consecutiveRefreshFailures++;
                await this.forceLogoutOnRefreshFailure();
            }
        }, this.config.refreshCheckIntervalMs);
    }
    stopAutoRefresh() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
    /**
     * Force logout when token refresh is exhausted.
     * Cleans up state and triggers the onRefreshExhausted callback.
     */
    async forceLogoutOnRefreshFailure() {
        this.stopAutoRefresh();
        // Clear token from HTTP client immediately
        this.http.setAuthToken('');
        // Clear storage
        if (this.storage) {
            await this.storage.removeUser();
        }
        // Dispatch the exhaustion action (sets isAuthenticated to false, clears user)
        this.dispatch({ type: 'REFRESH_EXHAUSTED' });
        // Fire the specific callback (app can show "session expired" message)
        this.config.onRefreshExhausted?.();
        // Also fire the onLogout callback (for cleanup like disconnecting presence)
        this.config.onLogout?.();
    }
    /**
     * Register a response interceptor that detects 401 responses
     * and triggers token refresh or forced logout as a safety net.
     */
    setup401Interceptor() {
        this.http.getInterceptors().addResponseInterceptor(async (ctx) => {
            if (ctx.status === 401) {
                const user = this.getUser();
                if (user?.refreshToken && !this.isRefreshing) {
                    // Attempt a single refresh so the next request succeeds
                    try {
                        console.warn('[AuthManager] 401 detected, attempting token refresh');
                        await this.refreshToken({ refreshToken: user.refreshToken });
                    }
                    catch {
                        // Refresh failed - if auto-refresh is disabled, force logout now.
                        // If auto-refresh is enabled, the timer retry logic will handle it.
                        if (!this.config.autoRefresh) {
                            await this.forceLogoutOnRefreshFailure();
                        }
                    }
                }
                else if (!user?.refreshToken && user) {
                    // Authenticated but no refresh token - force logout
                    console.warn('[AuthManager] 401 detected, no refresh token available, forcing logout');
                    await this.forceLogoutOnRefreshFailure();
                }
                // If isRefreshing is true, another refresh is already in progress.
                // The existing refreshPromise deduplication handles this.
            }
            return ctx;
        });
    }
    dispatch(action) {
        // Update internal state
        this.state = authReducer(this.state, action);
        // Update external state adapter
        if (this.stateAdapter) {
            if (this.stateAdapter.dispatch) {
                this.stateAdapter.dispatch(action);
            }
            else {
                this.stateAdapter.setState(this.state);
            }
        }
        // Notify listeners
        this.listeners.forEach((listener) => listener(this.state));
    }
}
//# sourceMappingURL=AuthManager.js.map